import {
  Body1,
  Caption1,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Textarea,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowDownload16Regular,
  ArrowReply16Regular,
  Attach16Regular,
  Delete16Regular,
  Dismiss12Regular,
  Dismiss24Regular,
  Document16Regular,
  DocumentPdf16Regular,
  Edit16Regular,
  History16Regular,
  Link16Regular,
} from "@fluentui/react-icons";
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { FuiButton } from "@/components/fui-control";
import type { AttachmentLink, ExecutionTask, TaskActivity, TaskComment } from "@/engine/types";
import { extractClipboardImageFiles } from "@/lib/clipboard-image";
import { formatAgo, formatDate } from "@/lib/date-format";
import { formatBytes } from "@/lib/format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { desktopDownloadClick } from "@/desktop/file-io";
import { CommentEmojiPicker } from "./CommentEmojiPicker";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    gap: tokens.spacingVerticalS,
  },
  stream: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: "4px",
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalS,
  },
  loadMoreWrap: {
    display: "flex",
    justifyContent: "center",
    padding: `${tokens.spacingVerticalXS} 0`,
    flexShrink: 0,
  },
  composerStickyWrap: {
    flexShrink: 0,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  emptyNotice: {
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalM}`,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // 댓글 단일 행 (호버 플로팅 액션의 앵커)
  commentRow: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
    ":focus-within": {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  commentDeleted: {
    opacity: 0.75,
  },
  commentFocused: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  commentAvatar: {
    flexShrink: 0,
    marginTop: "2px",
  },
  commentAvatarMuted: {
    opacity: 0.6,
  },
  commentContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  commentHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalS,
    minHeight: "20px",
  },
  authorName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  commentMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  commentBody: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: tokens.lineHeightBase300,
  },
  deletedBody: {
    fontSize: tokens.fontSizeBase200,
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalXXS} 0`,
  },
  // 플로팅 액션 바 (마우스 오버 및 키보드 포커스 시 노출)
  floatingActions: {
    position: "absolute",
    top: "-8px",
    right: "6px",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    gap: "2px",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    padding: "2px",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
    opacity: 0,
    pointerEvents: "none",
    transitionProperty: "opacity, transform",
    transitionDuration: "140ms",
    transform: "translateY(2px)",
  },
  floatingActionsVisible: {
    opacity: 1,
    pointerEvents: "auto",
    transform: "translateY(0)",
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "100ms",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
  actionBtnDanger: {
    ":hover": {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground1,
    },
  },
  // 대댓글 컨테이너 (2단계 락다운)
  repliesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    marginLeft: "18px",
    paddingLeft: "12px",
    ...shorthands.borderLeft("1px", "solid", tokens.colorNeutralStroke2),
    marginTop: tokens.spacingVerticalXS,
  },
  replyComposerWrap: {
    marginTop: tokens.spacingVerticalXS,
  },
  // 활동 로그 단일 행
  activityRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `2px ${tokens.spacingHorizontalXS}`,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    minHeight: "24px",
  },
  activityIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  activityText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  activityActor: {
    color: tokens.colorNeutralForeground1,
  },
  activityTime: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  // 첨부 파일 목록
  attachmentSection: {
    marginTop: tokens.spacingVerticalXXS,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  attachmentThumbnails: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },
  attachmentThumb: {
    position: "relative",
    width: "40px",
    height: "40px",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    overflow: "hidden",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: "pointer",
    padding: 0,
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transitionProperty: "opacity",
      transitionDuration: "120ms",
    },
    ":hover img": {
      opacity: 0.88,
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
  attachmentFiles: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  attachmentFileRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: "3px 6px",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    color: tokens.colorNeutralForeground1,
    textDecoration: "none",
    maxWidth: "100%",
    fontSize: tokens.fontSizeBase200,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  attachmentFileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  attachmentFileSize: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  // 컴포저 (입력기)
  composerCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
  },
  composerCardActive: {
    ...shorthands.border("1px", "solid", tokens.colorCompoundBrandStroke),
    boxShadow: "0 0 0 1px var(--colorCompoundBrandStroke)",
  },
  composerHead: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  composerTextarea: {
    width: "100%",
    minHeight: "48px",
    "& textarea": {
      fontSize: tokens.fontSizeBase200,
      fontWeight: tokens.fontWeightRegular,
    },
  },
  composerToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    marginTop: "2px",
  },
  composerToolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  composerToolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  stagedStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    paddingTop: tokens.spacingVerticalXXS,
  },
  stagedChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: "2px 6px",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground1,
  },
  stagedChipThumb: {
    width: "18px",
    height: "18px",
    ...shorthands.borderRadius("2px"),
    objectFit: "cover",
  },
  stagedChipRemove: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    background: "none",
    border: "none",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  lightboxSurface: {
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  lightboxImg: {
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  editActionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
});

const COMMENT_PAGE_SIZE = 50;
const COMMENT_IMAGE_MAX = 5;

function sortNewestFirst<T>(items: T[], timeOf: (item: T) => string): T[] {
  return [...items].sort((a, b) => new Date(timeOf(b)).getTime() - new Date(timeOf(a)).getTime());
}

function isImageAttachment(a: AttachmentLink): boolean {
  return (
    a.kind === "image" ||
    Boolean(a.mimeType?.startsWith("image/")) ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(a.url) ||
    a.url.startsWith("data:image/")
  );
}

function isLinkAttachment(a: AttachmentLink): boolean {
  if (isImageAttachment(a)) return false;
  if (a.kind === "link") return true;
  if (a.kind === "file") return false;
  return /^https?:/i.test(a.url) && a.size === undefined && !a.url.startsWith("data:");
}

export interface TaskActivityFeedProps {
  task: ExecutionTask;
  taskId: string;
  focusCommentId?: string | null;
  /** 댓글 작성·수정·삭제를 숨긴다. */
  readOnly?: boolean;
}

export const TaskActivityFeed = memo(function TaskActivityFeed({ task, taskId, focusCommentId, readOnly }: TaskActivityFeedProps) {
  const s = useProjectStore.getState;
  const handlers = useMemo<CommentThreadHandlers>(
    () => ({
      onAdd: (body, options) => s().addTaskComment(taskId, body, options),
      onUpdate: (commentId, body, options) => s().updateTaskComment(taskId, commentId, body, options),
      onDelete: (commentId) => s().deleteTaskComment(taskId, commentId),
    }),
    [s, taskId],
  );
  return <CommentThread comments={task.comments} focusCommentId={focusCommentId} readOnly={readOnly} {...handlers} />;
});

export interface CommentThreadHandlers {
  onAdd: (body: string, options?: { parentId?: string; attachments?: AttachmentLink[] }) => void;
  onUpdate: (commentId: string, body: string, options?: { attachments?: AttachmentLink[] }) => void;
  onDelete: (commentId: string) => void;
}

/** Threaded comments with replies, edit/delete tombstones and inline attachments. Store-agnostic. */
export const CommentThread = memo(function CommentThread({
  comments,
  onAdd,
  onUpdate,
  onDelete,
  focusCommentId,
  readOnly = false,
}: { comments: TaskComment[]; focusCommentId?: string | null; readOnly?: boolean } & CommentThreadHandlers) {
  const styles = useStyles();
  const me = useCatalogStore((st) => st.profile.displayName);

  const [replyingCommentId, setReplyingCommentId] = useState<string | null>(null);
  const [lightboxImg, setLightboxImg] = useState<AttachmentLink | null>(null);
  const [visibleCount, setVisibleCount] = useState(COMMENT_PAGE_SIZE);
  const streamTopRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const prevCommentsCountRef = useRef(comments.length);

  const { rootComments, replyMap } = useMemo(() => {
    const roots: TaskComment[] = [];
    const replies = new Map<string, TaskComment[]>();

    for (const c of comments) {
      if (!c.parentId) {
        roots.push(c);
      } else {
        const list = replies.get(c.parentId) ?? [];
        list.push(c);
        replies.set(c.parentId, list);
      }
    }

    roots.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    for (const [id, list] of replies) {
      replies.set(id, sortNewestFirst(list, (item) => item.at));
    }

    return { rootComments: roots, replyMap: replies };
  }, [comments]);

  const visibleRoots = rootComments.slice(0, visibleCount);
  const remaining = Math.max(0, rootComments.length - visibleCount);

  useEffect(() => {
    if (comments.length > prevCommentsCountRef.current) {
      streamTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    prevCommentsCountRef.current = comments.length;
  }, [comments.length]);

  useLayoutEffect(() => {
    if (!focusCommentId) return;
    const index = rootComments.findIndex((root) => root.id === focusCommentId || (replyMap.get(root.id) ?? []).some((reply) => reply.id === focusCommentId));
    if (index >= visibleCount) {
      setVisibleCount(index + 1);
      return;
    }
    streamRef.current?.querySelector(`[data-comment-id="${CSS.escape(focusCommentId)}"]`)?.scrollIntoView({ block: "center" });
  }, [focusCommentId, rootComments, replyMap, visibleCount]);

  const handleAddRootComment = useCallback((body: string, attachments?: AttachmentLink[]) => onAdd(body, { attachments }), [onAdd]);

  const handleAddReply = useCallback(
    (parentId: string, body: string, attachments?: AttachmentLink[]) => {
      onAdd(body, { parentId, attachments });
      setReplyingCommentId(null);
    },
    [onAdd],
  );

  const handleUpdateComment = useCallback(
    (commentId: string, newBody: string, attachments?: AttachmentLink[]) => onUpdate(commentId, newBody, { attachments }),
    [onUpdate],
  );

  const handleDeleteComment = useCallback((commentId: string) => onDelete(commentId), [onDelete]);

  return (
    <div className={styles.container}>
      {!readOnly && (
        <div className={styles.composerStickyWrap}>
          <CommentComposer
            authorName={me}
            placeholder={`${me}으로 댓글 남기기 · ⌘↩ 등록`}
            onSubmit={(body, attachments) => handleAddRootComment(body, attachments)}
          />
        </div>
      )}

      <div className={styles.stream} ref={streamRef}>
        <div ref={streamTopRef} />
        {visibleRoots.length === 0 ? (
          <div className={styles.emptyNotice}>등록된 댓글이 없습니다.</div>
        ) : (
          visibleRoots.map((root) => {
            const replies = replyMap.get(root.id) ?? [];
            return (
              <div key={root.id} style={{ display: "flex", flexDirection: "column" }}>
                <CommentRowItem
                  comment={root}
                  isReply={false}
                  focused={focusCommentId === root.id}
                  readOnly={readOnly}
                  canReply={!readOnly}
                  onReply={() => setReplyingCommentId(replyingCommentId === root.id ? null : root.id)}
                  onEdit={(newBody, attachments) => handleUpdateComment(root.id, newBody, attachments)}
                  onDelete={() => handleDeleteComment(root.id)}
                  onImageClick={(att) => setLightboxImg(att)}
                />

                {(replies.length > 0 || replyingCommentId === root.id) && (
                  <div className={styles.repliesContainer}>
                    {replies.map((reply) => (
                      <CommentRowItem
                        key={reply.id}
                        comment={reply}
                        isReply={true}
                        focused={focusCommentId === reply.id}
                        readOnly={readOnly}
                        canReply={false}
                        onEdit={(newBody, attachments) => handleUpdateComment(reply.id, newBody, attachments)}
                        onDelete={() => handleDeleteComment(reply.id)}
                        onImageClick={(att) => setLightboxImg(att)}
                      />
                    ))}

                    {!readOnly && replyingCommentId === root.id && (
                      <div className={styles.replyComposerWrap}>
                        <CommentComposer
                          authorName={me}
                          placeholder={`@${root.author} 님에게 답글 남기기 · ⌘↩ 등록`}
                          autoFocus
                          compact
                          showCancel
                          onCancel={() => setReplyingCommentId(null)}
                          onSubmit={(body, attachments) => handleAddReply(root.id, body, attachments)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {remaining > 0 && (
          <div className={styles.loadMoreWrap}>
            <FuiButton
              appearance="subtle"
              density="dialog"
              onClick={() => setVisibleCount((n) => n + COMMENT_PAGE_SIZE)}
              style={{ fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground2 }}
            >
              이전 댓글 {Math.min(COMMENT_PAGE_SIZE, remaining)}건 더보기
            </FuiButton>
          </div>
        )}
      </div>

      {/* 이미지 라이트박스 다이얼로그 */}
      {lightboxImg && (
        <Dialog open={true} onOpenChange={() => setLightboxImg(null)}>
          <DialogSurface className={styles.lightboxSurface}>
            <DialogTitle
              action={
                <FuiButton
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={() => setLightboxImg(null)}
                  aria-label="닫기"
                />
              }
            >
              {lightboxImg.title}
            </DialogTitle>
            <DialogContent style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: tokens.spacingVerticalM }}>
              <img src={lightboxImg.url} alt={lightboxImg.title} className={styles.lightboxImg} />
            </DialogContent>
            <DialogActions>
              <a href={lightboxImg.url} download={lightboxImg.title} onClick={desktopDownloadClick(lightboxImg.title, lightboxImg.url)} style={{ textDecoration: "none" }}>
                <FuiButton appearance="outline" icon={<ArrowDownload16Regular />}>
                  다운로드
                </FuiButton>
              </a>
            </DialogActions>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
});

/* ------------------------------------------------------------------------------------------------
 * 단일 댓글 행 컴포넌트 (플로팅 액션 바 + 수정 모드 + Tombstone + 첨부 파일 뷰어)
 * ---------------------------------------------------------------------------------------------- */
interface CommentRowItemProps {
  comment: TaskComment;
  isReply: boolean;
  focused?: boolean;
  readOnly?: boolean;
  canReply: boolean;
  onReply?: () => void;
  onEdit: (newBody: string, attachments?: AttachmentLink[]) => void;
  onDelete: () => void;
  onImageClick: (att: AttachmentLink) => void;
}

function CommentRowItem({
  comment,
  isReply,
  focused,
  readOnly = false,
  canReply,
  onReply,
  onEdit,
  onDelete,
  onImageClick,
}: CommentRowItemProps) {
  const styles = useStyles();
  const me = useCatalogStore((st) => st.profile.displayName);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isDeleted = Boolean(comment.deleted);
  const isEdited = Boolean(comment.updatedAt && comment.updatedAt > comment.at);

  const images = useMemo(
    () => (comment.attachments ?? []).filter(isImageAttachment).slice(0, COMMENT_IMAGE_MAX),
    [comment.attachments],
  );
  const links = useMemo(
    () => (comment.attachments ?? []).filter(isLinkAttachment),
    [comment.attachments],
  );
  const files = useMemo(
    () => (comment.attachments ?? []).filter((a) => !isImageAttachment(a) && !isLinkAttachment(a)),
    [comment.attachments],
  );

  return (
    <div
      data-comment-id={comment.id}
      className={mergeClasses(styles.commentRow, isDeleted && styles.commentDeleted, focused && styles.commentFocused)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setConfirmingDelete(false);
      }}
    >
      <div className={styles.commentAvatar}>
        <PersonAvatar name={comment.author} size={isReply ? 20 : 24} className={isDeleted ? styles.commentAvatarMuted : undefined} />
      </div>

      <div className={styles.commentContent}>
        <div className={styles.commentHeader}>
          <Caption1 className={styles.authorName}>{comment.author}</Caption1>
          <Caption1 className={styles.commentMeta} title={formatDate(comment.at, "numericDateTime")}>
            {formatAgo(comment.at)}
            {isEdited && !isDeleted ? " · (수정됨)" : ""}
          </Caption1>
        </div>

        {isDeleted ? (
          <div className={styles.deletedBody}>삭제된 댓글입니다.</div>
        ) : isEditing ? (
          <CommentComposer
            authorName={me}
            placeholder="댓글 수정 · ⌘↩ 저장"
            autoFocus
            compact
            showCancel
            submitLabel="저장"
            initialBody={comment.body}
            initialAttachments={comment.attachments}
            onCancel={() => setIsEditing(false)}
            onSubmit={(body, attachments) => {
              onEdit(body, attachments);
              setIsEditing(false);
            }}
          />
        ) : (
          <>
            {comment.body ? <Body1 className={styles.commentBody}>{comment.body}</Body1> : null}

            {(images.length > 0 || files.length > 0 || links.length > 0) && (
              <div className={styles.attachmentSection}>
                {images.length > 0 && (
                  <div className={styles.attachmentThumbnails}>
                    {images.map((img) => (
                      <button
                        key={img.id}
                        type="button"
                        className={styles.attachmentThumb}
                        onClick={() => onImageClick(img)}
                        title={img.title}
                        aria-label={`이미지 확대: ${img.title}`}
                      >
                        <img src={img.url} alt={img.title} />
                      </button>
                    ))}
                  </div>
                )}

                {files.length > 0 && (
                  <div className={styles.attachmentFiles}>
                    {files.map((f) => (
                      <a
                        key={f.id}
                        href={f.url}
                        download={f.title}
                        onClick={desktopDownloadClick(f.title, f.url)}
                        className={styles.attachmentFileRow}
                        title={f.title}
                      >
                        {f.title.endsWith(".pdf") ? (
                          <DocumentPdf16Regular style={{ flexShrink: 0, color: tokens.colorPaletteRedForeground1 }} />
                        ) : (
                          <Document16Regular style={{ flexShrink: 0, color: tokens.colorNeutralForeground2 }} />
                        )}
                        <span className={styles.attachmentFileName}>{f.title}</span>
                        {f.size ? <span className={styles.attachmentFileSize}>{formatBytes(f.size)}</span> : null}
                        <ArrowDownload16Regular style={{ flexShrink: 0, color: tokens.colorNeutralForeground3, width: 14, height: 14 }} />
                      </a>
                    ))}
                  </div>
                )}

                {links.length > 0 && (
                  <div className={styles.attachmentFiles}>
                    {links.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.attachmentFileRow}
                        title={link.url}
                      >
                        <Link16Regular style={{ flexShrink: 0, color: tokens.colorNeutralForeground2 }} />
                        <span className={styles.attachmentFileName}>{link.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {!readOnly && !isDeleted && !isEditing && (
        <div className={mergeClasses(styles.floatingActions, (isHovered || confirmingDelete) && styles.floatingActionsVisible)}>
          {confirmingDelete ? (
            <>
              <FuiButton appearance="secondary" density="dialog" onClick={() => setConfirmingDelete(false)}>
                취소
              </FuiButton>
              <FuiButton
                appearance="primary"
                density="dialog"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: tokens.colorNeutralForegroundOnBrand }}
                onClick={onDelete}
              >
                삭제
              </FuiButton>
            </>
          ) : (
            <>
              {canReply && onReply && (
                <Tooltip content="답글 달기" relationship="label">
                  <button type="button" className={styles.actionBtn} onClick={onReply} aria-label="답글 달기">
                    <ArrowReply16Regular style={{ width: 14, height: 14 }} />
                  </button>
                </Tooltip>
              )}
              <Tooltip content="수정" relationship="label">
                <button type="button" className={styles.actionBtn} onClick={() => setIsEditing(true)} aria-label="댓글 수정">
                  <Edit16Regular style={{ fontSize: 14 }} />
                </button>
              </Tooltip>
              <Tooltip content="삭제" relationship="label">
                <button
                  type="button"
                  className={mergeClasses(styles.actionBtn, styles.actionBtnDanger)}
                  onClick={() => setConfirmingDelete(true)}
                  aria-label="댓글 삭제"
                >
                  <Delete16Regular style={{ fontSize: 14 }} />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** 이미 저장된 활동 문구. 「만듦」은 새 기록부터 「생성」으로 남기므로, 예전 로그만 화면에서 맞춘다. */
function activityPhrase(message: string): string {
  return message.replaceAll("만듦", "생성");
}

export function TaskActivityLog({ items }: { items: TaskActivity[] }) {
  const styles = useStyles();
  const sorted = useMemo(() => sortNewestFirst(items, (item) => item.at), [items]);
  return (
    <div className={styles.container}>
      <div className={styles.stream}>
        {sorted.length === 0 ? (
          <div className={styles.emptyNotice}>등록된 활동이 없습니다.</div>
        ) : (
          sorted.map((activity) => (
            <div key={activity.id} className={styles.activityRow}>
              <span className={styles.activityIcon}>
                <History16Regular />
              </span>
              <span className={styles.activityText}>
                <span className={styles.activityActor}>{activity.actor}</span> · {activityPhrase(activity.message)}
              </span>
              <span className={styles.activityTime} title={formatDate(activity.at, "numericDateTime")}>{formatAgo(activity.at)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * 댓글 & 대댓글 작성기 (파일·링크 첨부, 이모지, 클립보드 이미지, 수정 시 첨부 관리)
 * ---------------------------------------------------------------------------------------------- */
interface CommentComposerProps {
  authorName: string;
  placeholder: string;
  autoFocus?: boolean;
  compact?: boolean;
  showCancel?: boolean;
  submitLabel?: string;
  initialBody?: string;
  initialAttachments?: AttachmentLink[];
  onCancel?: () => void;
  onSubmit: (body: string, attachments?: AttachmentLink[]) => void;
}

function normalizeCommentUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function CommentComposer({
  authorName,
  placeholder,
  autoFocus,
  compact,
  showCancel,
  submitLabel = "등록",
  initialBody = "",
  initialAttachments,
  onCancel,
  onSubmit,
}: CommentComposerProps) {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [body, setBody] = useState(initialBody);
  const [stagedFiles, setStagedFiles] = useState<AttachmentLink[]>(initialAttachments ?? []);
  const [isFocused, setIsFocused] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const imageCount = stagedFiles.filter(isImageAttachment).length;

  const appendAttachments = (items: AttachmentLink[]) => {
    setStagedFiles((prev) => {
      let images = prev.filter(isImageAttachment).length;
      const next = [...prev];
      for (const item of items) {
        if (isImageAttachment(item)) {
          if (images >= COMMENT_IMAGE_MAX) continue;
          images += 1;
        }
        next.push(item);
      }
      return next;
    });
  };

  const handleFiles = (fileList: FileList | null, imagesOnly = false) => {
    if (!fileList || fileList.length === 0) return;
    Array.from(fileList).forEach((file) => {
      const isImg = file.type.startsWith("image/");
      if (imagesOnly && !isImg) return;
      const reader = new FileReader();
      reader.onload = () => {
        appendAttachments([
          {
            id: `att-comment-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
            title: file.name,
            url: reader.result as string,
            kind: isImg ? "image" : "file",
            size: file.size,
            mimeType: file.type,
            addedAt: new Date().toISOString(),
            ...(authorName.trim() ? { addedBy: authorName.trim() } : {}),
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const images = extractClipboardImageFiles(e.clipboardData);
    if (images.length === 0) return;
    e.preventDefault();
    const dt = new DataTransfer();
    images.forEach((file) => dt.items.add(file));
    handleFiles(dt.files, true);
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setBody((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const commitLink = () => {
    const url = normalizeCommentUrl(linkUrl);
    if (!url) return;
    appendAttachments([
      {
        id: `att-comment-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        title: linkTitle.trim() || url.replace(/^https?:\/\//, ""),
        url,
        kind: "link",
        addedAt: new Date().toISOString(),
        ...(authorName.trim() ? { addedBy: authorName.trim() } : {}),
      },
    ]);
    setLinkTitle("");
    setLinkUrl("");
    setLinkOpen(false);
  };

  const handleSubmit = () => {
    const trimmed = body.trim();
    if (!trimmed && stagedFiles.length === 0) return;
    onSubmit(trimmed, stagedFiles.length > 0 ? stagedFiles : undefined);
    setBody("");
    setStagedFiles([]);
  };

  const canSubmit = body.trim().length > 0 || stagedFiles.length > 0;

  return (
    <div
      className={mergeClasses(styles.composerCard, isFocused && styles.composerCardActive)}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleFiles(e.dataTransfer.files);
      }}
    >
      <div className={styles.composerHead}>
        <PersonAvatar name={authorName} size={compact ? 20 : 24} tip={false} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Textarea
            rows={2}
            resize="vertical"
            value={body}
            placeholder={placeholder}
            aria-label={placeholder}
            autoFocus={autoFocus}
            className={styles.composerTextarea}
            textarea={{ ref: textareaRef }}
            onChange={(_, data) => setBody(data.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              } else if (e.key === "Escape" && showCancel && onCancel) {
                e.preventDefault();
                onCancel();
              }
            }}
          />
        </div>
      </div>

      {stagedFiles.length > 0 && (
        <div className={styles.stagedStrip}>
          {stagedFiles.map((f) => (
            <span key={f.id} className={styles.stagedChip}>
              {isImageAttachment(f) ? (
                <img src={f.url} alt="" className={styles.stagedChipThumb} />
              ) : isLinkAttachment(f) ? (
                <Link16Regular style={{ fontSize: 13, color: tokens.colorNeutralForeground2 }} />
              ) : (
                <Document16Regular style={{ fontSize: 13, color: tokens.colorNeutralForeground2 }} />
              )}
              <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.title}
              </span>
              {f.size ? <span style={{ color: tokens.colorNeutralForeground3, fontSize: 10 }}>({formatBytes(f.size)})</span> : null}
              <button
                type="button"
                className={styles.stagedChipRemove}
                onClick={() => setStagedFiles((prev) => prev.filter((item) => item.id !== f.id))}
                aria-label={`첨부 제거: ${f.title}`}
              >
                <Dismiss12Regular />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.composerToolbar}>
        <div className={styles.composerToolbarLeft}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <Tooltip content={imageCount >= COMMENT_IMAGE_MAX ? `이미지는 최대 ${COMMENT_IMAGE_MAX}장` : "파일 첨부 · 이미지는 ⌘V"} relationship="label">
            <FuiButton
              appearance="subtle"
              density="dialog"
              icon={<Attach16Regular />}
              onClick={() => fileInputRef.current?.click()}
              aria-label="파일 첨부"
            />
          </Tooltip>
          <Popover open={linkOpen} onOpenChange={(_, d) => setLinkOpen(d.open)} positioning="below-start">
            <PopoverTrigger disableButtonEnhancement>
              <Tooltip content="링크 첨부" relationship="label">
                <FuiButton appearance="subtle" density="dialog" icon={<Link16Regular />} aria-label="링크 첨부" />
              </Tooltip>
            </PopoverTrigger>
            <PopoverSurface style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, width: 240, padding: tokens.spacingVerticalS }}>
              <Input size="small" value={linkTitle} placeholder="제목 (선택)" onChange={(_, d) => setLinkTitle(d.value)} />
              <Input size="small" value={linkUrl} placeholder="https://" onChange={(_, d) => setLinkUrl(d.value)} />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: tokens.spacingHorizontalXS }}>
                <FuiButton appearance="secondary" density="dialog" onClick={() => setLinkOpen(false)}>
                  취소
                </FuiButton>
                <FuiButton appearance="primary" density="dialog" disabled={!linkUrl.trim()} onClick={commitLink}>
                  추가
                </FuiButton>
              </div>
            </PopoverSurface>
          </Popover>
          <CommentEmojiPicker onPick={insertEmoji} />
        </div>

        <div className={styles.composerToolbarRight}>
          {showCancel && onCancel && (
            <FuiButton appearance="secondary" density="dialog" onClick={onCancel}>
              취소
            </FuiButton>
          )}
          <FuiButton appearance="primary" density="dialog" disabled={!canSubmit} onClick={handleSubmit}>
            {submitLabel}
          </FuiButton>
        </div>
      </div>
    </div>
  );
}
